(() => {
  const hero = document.querySelector('[data-jsca-hero]');
  const header = document.querySelector('[data-jsca-header]');
  const media = document.querySelector('[data-hero-media]');
  const overlay = document.querySelector('[data-archive-overlay]');
  const archiveOpen = document.querySelector('[data-archive-open]');
  const archiveClosers = [...document.querySelectorAll('[data-archive-close]')];
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const accessForm = document.querySelector('[data-access-form]');

  const primeAutoplayVideo = (videoElement) => {
    if (!videoElement) return;

    videoElement.muted = true;
    videoElement.defaultMuted = true;
    videoElement.autoplay = true;
    videoElement.loop = true;
    videoElement.playsInline = true;
    videoElement.controls = false;
    videoElement.disablePictureInPicture = true;
    videoElement.setAttribute('muted', '');
    videoElement.setAttribute('autoplay', '');
    videoElement.setAttribute('loop', '');
    videoElement.setAttribute('playsinline', '');
    videoElement.setAttribute('webkit-playsinline', '');
    videoElement.setAttribute('controlslist', 'nodownload nofullscreen noremoteplayback');
    videoElement.setAttribute('disablepictureinpicture', '');
    videoElement.removeAttribute('controls');

    const playVideo = () => {
      if (document.visibilityState === 'hidden' || videoElement.readyState < 2) return;
      const playAttempt = videoElement.play();
      if (playAttempt && typeof playAttempt.catch === 'function') playAttempt.catch(() => {});
    };

    videoElement.addEventListener('loadeddata', playVideo, { once: true });
    videoElement.addEventListener('canplay', playVideo, { once: true });
    window.addEventListener('pageshow', playVideo);
    document.addEventListener('visibilitychange', playVideo);
    ['pointerdown', 'touchstart', 'click'].forEach((eventName) => {
      document.addEventListener(eventName, playVideo, { once: true, passive: true });
    });

    playVideo();
  };

  if (accessForm) {
    // Design decision: local-only prototype state. Do not create a Shopify customer/email mutation
    // until the approved email/SMS backend and consent language are selected.
    const status = accessForm.querySelector('[data-access-status]');
    const submit = accessForm.querySelector('button[type="submit"] span');
    accessForm.addEventListener('submit', (event) => {
      event.preventDefault();
      const input = accessForm.querySelector('input[type="email"]');
      if (!input || !input.checkValidity()) {
        if (status) status.textContent = 'Enter a valid email for Drop 1 updates.';
        if (input) input.reportValidity();
        return;
      }
      accessForm.classList.add('is-complete');
      if (submit) submit.textContent = 'Update Saved';
      if (status) status.textContent = 'You are set for the first Drop 1 update.';
    });
  }

  [...document.querySelectorAll('[data-jsca-autoplay-video], .jsca-hero__video')].forEach(primeAutoplayVideo);

  // Design decision: the Archive control behaves like a focused cinematic door.
  // It keeps the user inside the approved opening-frame world until deeper destinations are built.
  if (overlay && archiveOpen) {
    const focusableSelector = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
    let previousFocus = null;

    const getFocusable = () => [...overlay.querySelectorAll(focusableSelector)].filter((el) => el.offsetParent !== null);

    const openArchive = () => {
      previousFocus = document.activeElement;
      overlay.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');
      archiveOpen.setAttribute('aria-expanded', 'true');
      document.body.classList.add('jsca-archive-open');
      window.setTimeout(() => {
        const first = getFocusable()[0];
        if (first) first.focus({ preventScroll: true });
      }, reduceMotion ? 0 : 220);
    };

    const closeArchive = ({ restoreFocus = true } = {}) => {
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      archiveOpen.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('jsca-archive-open');
      if (restoreFocus && previousFocus && typeof previousFocus.focus === 'function') {
        previousFocus.focus({ preventScroll: true });
      }
    };

    archiveOpen.addEventListener('click', openArchive);
    archiveClosers.forEach((control) => {
      control.addEventListener('click', () => closeArchive({ restoreFocus: control.tagName !== 'A' }));
    });

    document.addEventListener('keydown', (event) => {
      if (!overlay.classList.contains('is-open')) return;
      if (event.key === 'Escape') closeArchive();
      if (event.key !== 'Tab') return;

      const focusable = getFocusable();
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  const thresholdRevealEls = [...document.querySelectorAll('[data-threshold-reveal]')];
  if (thresholdRevealEls.length) {
    if (reduceMotion || !('IntersectionObserver' in window)) {
      thresholdRevealEls.forEach((el) => el.classList.add('is-visible'));
    } else {
      // Design decision: reveal the archive threshold like a gallery room coming into view,
      // not like a scroll-jacking ecommerce section.
      const thresholdObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          thresholdObserver.unobserve(entry.target);
        });
      }, { threshold: 0.18, rootMargin: '0px 0px -10% 0px' });
      thresholdRevealEls.forEach((el) => thresholdObserver.observe(el));
    }
  }

  if (!hero || reduceMotion) return;

  let ticking = false;
  let mouseX = 0;
  let mouseY = 0;

  // Design decision: scroll and cursor motion are intentionally tiny so the frame feels alive,
  // not interactive in a SaaS/startup way.
  const render = () => {
    const progress = Math.min(1, Math.max(0, window.scrollY / Math.max(1, window.innerHeight)));
    hero.style.setProperty('--scroll-progress', String(progress));
    hero.style.setProperty('--mouse-x', mouseX.toFixed(4));
    hero.style.setProperty('--mouse-y', mouseY.toFixed(4));
    hero.style.setProperty('--parallax-x', `${50 + mouseX * 1.2}%`);
    hero.style.setProperty('--parallax-y', `${50 + mouseY * 1.2}%`);

    if (media) {
      const scale = 1.018 + progress * 0.032;
      media.style.transform = `scale(${scale}) translate3d(${mouseX * -5}px, ${progress * 20 + mouseY * -4}px, 0)`;
    }
    if (header) header.style.opacity = String(Math.max(0.48, 1 - progress * 0.45));
    ticking = false;
  };

  const requestRender = () => {
    if (!ticking) {
      window.requestAnimationFrame(render);
      ticking = true;
    }
  };

  window.addEventListener('scroll', requestRender, { passive: true });
  window.addEventListener('pointermove', (event) => {
    if (event.pointerType === 'touch') return;
    mouseX = (event.clientX / window.innerWidth - 0.5) * 2;
    mouseY = (event.clientY / window.innerHeight - 0.5) * 2;
    requestRender();
  }, { passive: true });

  render();
})();
